import { ethers } from 'ethers';
import { toast } from 'react-hot-toast';
import dUSDTokenABI from '@/contracts/dUSDToken.json';
import FTTFTokenABI from '@/contracts/FTTFToken.json';

// Token addresses
const DUSD_TOKEN_ADDRESS = import.meta.env.VITE_TOKEN_ADDRESS || '0xB7Ae78C7f2B522882aFBebE60f68687b941ebEb2';
const FTTF_TOKEN_ADDRESS = import.meta.env.VITE_NFT_ADDRESS || '0x0e694875e9376A00A8222CD4E769b61B4416CD43';

// Faucet private key (admin account)
const FAUCET_PRIVATE_KEY = '0x8166f546bab6da521a8369cab06c5d2b9e46670292d85c875ee9ec20e84ffb61';

// RPC URL
const RPC_URL = 'https://rpc.blaze.soniclabs.com';

/**
 * Send tokens to a recipient
 * @param recipientAddress Recipient's wallet address
 * @param amount Amount to send
 * @param tokenType 'DUSD' or 'FTTF'
 * @returns Transaction hash
 */
export const sendTokens = async (
  recipientAddress: string,
  amount: string,
  tokenType: 'DUSD' | 'FTTF' = 'DUSD'
): Promise<string> => {
  try {
    // Create provider and wallet
    const provider = new ethers.JsonRpcProvider(RPC_URL);
    const wallet = new ethers.Wallet(FAUCET_PRIVATE_KEY, provider);
    
    // Select token address based on type
    const tokenAddress = tokenType === 'DUSD' ? DUSD_TOKEN_ADDRESS : FTTF_TOKEN_ADDRESS;
    const tokenABI = tokenType === 'DUSD' ? dUSDTokenABI : FTTFTokenABI;
    
    // Create contract instance
    const tokenContract = new ethers.Contract(tokenAddress, tokenABI, wallet);
    
    // Convert amount to wei (18 decimals)
    const amountInWei = ethers.parseUnits(amount, 18);
    
    // Send transaction
    const tx = await tokenContract.transfer(recipientAddress, amountInWei);
    await tx.wait();
    
    return tx.hash;
  } catch (error) {
    console.error(`Error sending ${tokenType}:`, error);
    throw error;
  }
};

/**
 * Send a tip to a creator
 * @param creatorAddress Creator's wallet address
 * @param amount Amount to tip
 * @returns Transaction hash and status
 */
export const sendTip = async (
  creatorAddress: string,
  amount: string
): Promise<{ txHash: string; status: 'success' | 'error' }> => {
  try {
    const txHash = await sendTokens(creatorAddress, amount, 'DUSD');
    
    return {
      txHash,
      status: 'success'
    };
  } catch (error) {
    console.error('Error sending tip:', error);
    return {
      txHash: '',
      status: 'error'
    };
  }
};

/**
 * Purchase an NFT
 * @param nftContractAddress NFT contract address
 * @param tokenId Token ID to purchase
 * @param price Price in DUSD
 * @param sellerAddress Seller's wallet address
 * @returns Transaction details
 */
export const purchaseNFT = async (
  nftContractAddress: string,
  tokenId: string,
  price: string,
  sellerAddress: string
): Promise<{ 
  txHash: string; 
  status: 'success' | 'error';
  message?: string;
}> => {
  try {
    // In a real implementation, this would:
    // 1. Approve the marketplace contract to spend DUSD
    // 2. Call the marketplace purchase function
    
    // For demo, we'll just simulate a transfer to the seller
    const txHash = await sendTokens(sellerAddress, price, 'DUSD');
    
    return {
      txHash,
      status: 'success',
      message: `Successfully purchased NFT #${tokenId}`
    };
  } catch (error) {
    console.error('Error purchasing NFT:', error);
    return {
      txHash: '',
      status: 'error',
      message: 'Failed to purchase NFT. Please try again.'
    };
  }
};

/**
 * Request tokens from the faucet
 * @param recipientAddress Recipient's wallet address
 * @returns Transaction details for both token types
 */
export const requestFaucetTokens = async (
  recipientAddress: string
): Promise<{
  dusdTxHash: string;
  fttfTxHash: string;
  status: 'success' | 'partial' | 'error';
}> => {
  try {
    // Send 1000 DUSD
    const dusdTxHash = await sendTokens(recipientAddress, '1000', 'DUSD');
    
    // Send 1000 FTTF
    const fttfTxHash = await sendTokens(recipientAddress, '1000', 'FTTF');
    
    return {
      dusdTxHash,
      fttfTxHash,
      status: 'success'
    };
  } catch (error) {
    console.error('Error requesting faucet tokens:', error);
    return {
      dusdTxHash: '',
      fttfTxHash: '',
      status: 'error'
    };
  }
};

/**
 * Get token balances for an address
 * @param address Wallet address
 * @returns Token balances
 */
export const getTokenBalances = async (
  address: string
): Promise<{
  dusd: string;
  fttf: string;
}> => {
  try {
    // Create provider
    const provider = new ethers.JsonRpcProvider(RPC_URL);
    
    // Create contract instances
    const dusdContract = new ethers.Contract(DUSD_TOKEN_ADDRESS, dUSDTokenABI, provider);
    const fttfContract = new ethers.Contract(FTTF_TOKEN_ADDRESS, FTTFTokenABI, provider);
    
    // Get balances
    const dusdBalance = await dusdContract.balanceOf(address);
    const fttfBalance = await fttfContract.balanceOf(address);
    
    // Format balances
    const dusdFormatted = ethers.formatUnits(dusdBalance, 18);
    const fttfFormatted = ethers.formatUnits(fttfBalance, 18);
    
    return {
      dusd: dusdFormatted,
      fttf: fttfFormatted
    };
  } catch (error) {
    console.error('Error getting token balances:', error);
    return {
      dusd: '0',
      fttf: '0'
    };
  }
};

// Demo mode functions that don't actually interact with the blockchain
export const demoMode = {
  sendTip: async (creatorAddress: string, amount: string): Promise<{ txHash: string; status: 'success' | 'error' }> => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Generate random transaction hash
    const txHash = '0x' + Array.from({length: 64}, () => Math.floor(Math.random() * 16).toString(16)).join('');
    
    toast.success(`Sent ${amount} DUSD tip successfully!`);
    
    return {
      txHash,
      status: 'success'
    };
  },
  
  purchaseNFT: async (
    nftContractAddress: string,
    tokenId: string,
    price: string,
    sellerAddress: string
  ): Promise<{ 
    txHash: string; 
    status: 'success' | 'error';
    message?: string;
  }> => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    // Generate random transaction hash
    const txHash = '0x' + Array.from({length: 64}, () => Math.floor(Math.random() * 16).toString(16)).join('');
    
    toast.success(`Successfully purchased NFT #${tokenId} for ${price} DUSD`);
    
    return {
      txHash,
      status: 'success',
      message: `Successfully purchased NFT #${tokenId}`
    };
  },
  
  requestFaucetTokens: async (recipientAddress: string): Promise<{
    dusdTxHash: string;
    fttfTxHash: string;
    status: 'success' | 'partial' | 'error';
  }> => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 2500));
    
    // Generate random transaction hashes
    const dusdTxHash = '0x' + Array.from({length: 64}, () => Math.floor(Math.random() * 16).toString(16)).join('');
    const fttfTxHash = '0x' + Array.from({length: 64}, () => Math.floor(Math.random() * 16).toString(16)).join('');
    
    toast.success('Successfully received 1000 DUSD and 1000 FTTF tokens!');
    
    return {
      dusdTxHash,
      fttfTxHash,
      status: 'success'
    };
  }
};

export default {
  sendTokens,
  sendTip,
  purchaseNFT,
  requestFaucetTokens,
  getTokenBalances,
  demoMode
};